import { IfAbility } from '@efdigital/ll-addon-auth';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import LtiCreateDeploymentModal from '@efdigital/ll-admin-v2/components/lti/create-deployment-modal.gts';
import { EditLtiConsumedToolDeployment } from '@efdigital/ll-admin-v2/components/lti/edit-lti-consumed-tool-deployment.gts';
import { buildDeleteConsumedToolDeployment } from '@efdigital/ll-admin-v2/data/builders/lti/lti-consumed-tool-deployments';
import { buildListResourceLinks } from '@efdigital/ll-admin-v2/data/builders/lti/lti-resource-links';
import type { LtiConsumedToolDeployment } from '@efdigital/ll-admin-v2/data/types/lti';
import type { LibraryClientsEditLtiIntegrationsPlatformModel } from '@efdigital/ll-admin-v2/routes/library/clients/client/edit/lti-integrations/platform';
import {
  alertLtiDeleteError,
  buildDeploymentDeleteConfirmMessage,
} from '@efdigital/ll-admin-v2/utils/lti/delete-errors';
import {
  findXceedTool,
  XCEED_PRODUCT,
} from '@efdigital/ll-admin-v2/utils/lti/products/xceed';
import { toolPhaseStatus } from '@efdigital/ll-admin-v2/utils/lti/setup-status';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface Signature {
  Args: {
    model: LibraryClientsEditLtiIntegrationsPlatformModel;
  };
}

export default class LtiIntegrationsPlatformDeploymentsTemplate extends Component<Signature> {
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked private _deletingId: string | null = null;

  get tool() {
    return findXceedTool(this.args.model.tools);
  }

  get deployments(): LtiConsumedToolDeployment[] {
    if (!this.tool) {
      return [];
    }

    return this.args.model.deploymentsByToolId[this.tool.id] ?? [];
  }

  get toolPhaseComplete(): boolean {
    return toolPhaseStatus(this.tool) === 'complete';
  }

  handleDeploymentCreated = (_deployment: LtiConsumedToolDeployment) => {
    void this.router.refresh(
      'library.clients.client.edit.lti-integrations.platform'
    );
  };

  isDeletingDeployment = (deployment: LtiConsumedToolDeployment): boolean =>
    this._deletingId === deployment.id;

  isDeploymentDeleteDisabled = (
    deployment: LtiConsumedToolDeployment,
    isEditing: boolean
  ): boolean => isEditing || this.isDeletingDeployment(deployment);

  deleteDeployment = async (deployment: LtiConsumedToolDeployment) => {
    const tool = this.tool;
    if (!tool) {
      return;
    }

    let confirmMessage = `Are you sure you want to delete deployment ${deployment.platformDeploymentId}?`;

    try {
      const resourceLinks = (
        await this.store.request(
          buildListResourceLinks(
            this.args.model.platform.id,
            tool.id,
            deployment.id
          )
        )
      ).content.data;

      confirmMessage = buildDeploymentDeleteConfirmMessage(
        deployment.platformDeploymentId,
        resourceLinks
      );
    } catch (error) {
      alertLtiDeleteError(error);
      return;
    }

    if (!confirm(confirmMessage)) {
      return;
    }

    this._deletingId = deployment.id;
    try {
      await this.store.request(
        buildDeleteConsumedToolDeployment(
          deployment,
          this.args.model.platform.id,
          tool.id
        )
      );
      void this.router.refresh(
        'library.clients.client.edit.lti-integrations.platform'
      );
    } catch (error) {
      alertLtiDeleteError(error);
    } finally {
      this._deletingId = null;
    }
  };

  <template>
    <div data-test-lti-phase-deployments>
      {{#unless this.toolPhaseComplete}}
        <p class="text-muted mb-4">
          Complete phase 2 before adding deployments.
        </p>
      {{/unless}}

      {{#if this.tool}}
        <EditContext as |context|>
          <p class="text-muted">
            The customer will deploy
            {{XCEED_PRODUCT.name}}
            one or more times in their LMS and provide an id for each
            deployment. Configure each deployment record below.
          </p>

          {{#if this.deployments.length}}
            {{#each this.deployments as |deployment|}}
              <div class="card shadow-sm mb-4">
                <div
                  class="card-header bg-transparent d-flex justify-content-between align-items-center"
                >
                  <span class="fw-semibold font-monospace">
                    Deployment
                  </span>
                  <IfAbility @ability="lti-integrations" @atLeast="editor">
                    <:if>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        disabled={{this.isDeploymentDeleteDisabled
                          deployment
                          context.isEditing
                        }}
                        data-test-lti-delete-deployment
                        {{on "click" (fn this.deleteDeployment deployment)}}
                      >
                        {{#if (this.isDeletingDeployment deployment)}}
                          Deleting…
                        {{else}}
                          <iconify-icon inline icon="ph:trash" />
                        {{/if}}
                      </button>
                    </:if>
                  </IfAbility>
                </div>
                <div class="card-body">
                  <EditLtiConsumedToolDeployment
                    @item={{deployment}}
                    @platformId={{@model.platform.id}}
                    @consumedToolId={{this.tool.id}}
                    @context={{context}}
                    @initialState="readonly"
                  />
                </div>
              </div>
            {{/each}}
          {{else}}
            <p class="text-muted">No deployments configured yet.</p>
          {{/if}}

          <IfAbility @ability="lti-integrations" @atLeast="editor">
            <:if>
              <LtiCreateDeploymentModal
                @platformId={{@model.platform.id}}
                @consumedToolId={{this.tool.id}}
                @onSaved={{this.handleDeploymentCreated}}
              >
                <:trigger as |openModal|>
                  <button
                    type="button"
                    class="btn btn-primary"
                    {{on "click" openModal}}
                    data-test-lti-add-deployment
                  >
                    <iconify-icon inline icon="ph:plus" />
                    Add deployment
                  </button>
                </:trigger>
              </LtiCreateDeploymentModal>
            </:if>
          </IfAbility>
        </EditContext>
      {{else}}
        <p class="text-muted mb-0">
          Register
          {{XCEED_PRODUCT.name}}
          in phase 2 to add deployments.
        </p>
      {{/if}}
    </div>
  </template>
}
